"use client";

import { useState, useSyncExternalStore } from "react";

import type { BrochureDownloadResponse } from "@tripcart/shared";

import { useTranslator, type Translator } from "@/lib/translation-context";

export interface LeadDetails {
  name: string;
  email: string;
}

/** Shown inline: the only `<Toaster>` is in the `sites/[agency]` tree, so a
 *  toast on the `(public)` tree renders nothing (storefront LESSONS). */
export interface DownloadStatus {
  kind: "success" | "error";
  message: string;
}

class DownloadFailedError extends Error {}

/** Why a download failed. 4xx messages from the API are written for the
 *  traveler (AC-14 names the field to fix); anything else gets our own line. */
async function failureMessage(res: Response, t: Translator): Promise<string> {
  if (res.status === 429) return t("Please wait a moment before trying again.");
  const body = (await res.json().catch(() => ({}))) as {
    error?: { message?: string };
  };
  if (res.status < 500 && body.error?.message) return body.error.message;
  return t("We could not start the download. Please try again.");
}

const storageKey = (slug: string) => `tc-brochure-lead-${slug}`;

/** The details a traveler already gave for each tour. Kept so a second click
 *  downloads at once (prototype "unlocked") while the API still gets the
 *  details it requires when lead collection is on. The map is the store
 *  `useSyncExternalStore` reads (a stable snapshot per tour); localStorage is
 *  what survives a reload. */
const remembered = new Map<string, LeadDetails | null>();
const listeners = new Set<() => void>();

function readLead(slug: string): LeadDetails | null {
  if (!remembered.has(slug)) {
    try {
      remembered.set(
        slug,
        JSON.parse(localStorage.getItem(storageKey(slug)) ?? "null"),
      );
    } catch {
      remembered.set(slug, null);
    }
  }
  return remembered.get(slug) ?? null;
}

function rememberLead(slug: string, lead: LeadDetails): void {
  remembered.set(slug, lead);
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(lead));
  } catch {
    // Private mode: unlocked for this visit, the popup asks again next time.
  }
  listeners.forEach((notify) => notify());
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

function startDownload({ url, fileName }: BrochureDownloadResponse): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function requestDownload(
  slug: string,
  lead: LeadDetails | null,
  t: Translator,
) {
  const res = await fetch(
    `/api/tours/${encodeURIComponent(slug)}/brochure/download/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(lead ?? {}),
    },
  );
  if (!res.ok) throw new DownloadFailedError(await failureMessage(res, t));
  return ((await res.json()) as { data: BrochureDownloadResponse }).data;
}

export function useBrochureDownload(slug: string, collectLeads: boolean) {
  const t = useTranslator();
  // Server snapshot is null: localStorage is client-only.
  const lead = useSyncExternalStore(
    subscribe,
    () => readLead(slug),
    () => null,
  );
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<DownloadStatus | null>(null);
  const locked = collectLeads && !lead;

  async function download(details: LeadDetails | null): Promise<boolean> {
    setBusy(true);
    setStatus(null);
    try {
      startDownload(
        await requestDownload(slug, collectLeads ? details : null, t),
      );
      if (collectLeads && details) rememberLead(slug, details);
      setStatus({
        kind: "success",
        message: details
          ? t(
              "Your brochure is downloading. Check your downloads folder. It works offline too.",
            )
          : t("Your brochure is downloading. Check your downloads folder."),
      });
      return true;
    } catch (err) {
      setStatus({
        kind: "error",
        message:
          err instanceof DownloadFailedError
            ? err.message
            : t("We could not start the download. Please try again."),
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { locked, busy, lead, status, download };
}
